import { useEffect, useRef, useState } from "react";
import { CustomLoader } from "../custom-loader/custom-loader";
import { FormInput } from "../form-component/form-input";
import { CUSTOM_VALIDATION } from "../../validation/validation";
import { Toast } from "primereact/toast";
import { FormDropdown } from "../form-component/form-dropdown";
import { CustomToast } from "../alert/custom-toast";
import { HELPER } from "../../helper/helper";
import { SERVICES } from "../../../core/services/services";
import { Optional } from "../optional/optional";
import { UserTypes } from "../../constants";
import { getDateRangeValidationStatus } from "../../helper/date-validation";

export function TransactionsFilter(props) {
  const toast = useRef(null);
  const [loading] = useState(false);
  const [currentIndex] = useState(0);
  const [dateRangeError, setDateRangeError] = useState(null);
  const [errorMessage] = useState(null);
  const [aggregators, setAggregators] = useState([]);
  const [dateRangeValidation, setDateRangeValidation] = useState({
    isValid: true,
    dayCount: 0,
    hasStartDate: false,
    hasEndDate: false,
    errorMessage: null,
  });
  const [transactionFilters, setTransactionFilters] = useState({
    accountNumber: "",
    paymentFacilitatorId: "",
    status: "",
    amount: "",
    transactionReference: "",
    facilitatorTransactionReference: "",
    transactionEndDate: "",
    transactionStartDate: "",
  });
  const [formErrors, setFormError] = useState({
    accountNumber: null,
    paymentFacilitatorId: null,
    status: null,
    amount: null,
    transactionReference: null,
    facilitatorTransactionReference: null,
    transactionEndDate: null,
    transactionStartDate: null,
  });
  const transactionStatuses = ["SUCCESSFUL", "FAILED", "PENDING"];

  useEffect(() => {
    function getPaymentFacilitators() {
      SERVICES.GET_ALL_MERCHANTS()
        .then((data) => {
          const result = data?.result;
          setAggregators(result);
        })
        .catch((error) => {
          toast.current.show({
            severity: "error",
            summary: "Error getting merchants!",
            detail: HELPER.PROCESS_ERROR(error, "TOAST"),
            life: 10000,
          });
        });
    }
    if (props.userType === UserTypes.Backoffice) {
      getPaymentFacilitators();
    }
  }, [props.userType]);

function validateForm(e, name, type, field, required) {
  let value = e?.target?.value;
  if (name === "transactionStartDate" || name === "transactionEndDate") {
    const updatedFilters = { ...transactionFilters, [name]: value };

    // Get real-time validation status - always use 90 days
    const validation = getDateRangeValidationStatus(
      updatedFilters.transactionStartDate,
      updatedFilters.transactionEndDate,
      90
    );

    setDateRangeValidation(validation);
    setDateRangeError(validation.errorMessage);

    // Update filter value
    setTransactionFilters(updatedFilters);
    setFormError((prev) => ({
      ...prev,
      transactionStartDate: null,
      transactionEndDate: null,
    }));

    return;
  }
  const isEmpty = CUSTOM_VALIDATION.IS_EMPTY(value);
  const isValidInput = !isEmpty
    ? CUSTOM_VALIDATION.BASIC_VALIDATION(value, type)
    : false;
    if (isValidInput) {
      setTransactionFilters({ ...transactionFilters, [name]: value });
      setFormError({ ...formErrors, [name]: null });
    } else {
      let errorMessage = required && isEmpty ? `${field} is required` : null;
      if (!isValidInput) {
        errorMessage = `${field} is invalid`;
      }
      if (isEmpty && !required) {
        setTransactionFilters({ ...transactionFilters, [name]: "" });
        setFormError({ ...formErrors, [name]: null });
      } else {
        setFormError({
          ...formErrors,
          [name]: errorMessage,
        });
      }
    }
  }

  function getFilters() {
    let payload = {};
    for (const property in transactionFilters) {
      if (transactionFilters[property]) {
        if (property === "amount") {
          payload[property] = HELPER.TO_MINOR_AMOUNT(
            transactionFilters[property]
          );
        } else if (property === "paymentFacilitatorId") {
          payload[property] =
            transactionFilters["paymentFacilitatorId"]?.paymentFacilitatorId;
        } else {
          payload[property] = transactionFilters[property];
        }
      }
    }
    return payload;
  }

  function cancelModal() {
    props.closeModal();
  }

  function validateDropdown(e, name) {
    const value = e.target.value;
    if (value) {
      setFormError({ ...formErrors, [name]: null });
      setTransactionFilters({ ...transactionFilters, [name]: value });
    }
  }

  const cancelButton = () => {
    if (!loading) {
      return (
        <button onClick={cancelModal} className="secondary-button">
          Cancel
        </button>
      );
    } else {
      return <div />;
    }
  };

  const submitButton = () => {
    if (!loading) {
      const hasFilters = HELPER.HAS_FILTER_VALUES(transactionFilters);
      const isDisabled = !dateRangeValidation.isValid || !hasFilters;

      return (
        <button
          onClick={() => {
            if (!isDisabled) {
              props?.searchFunction(getFilters());
            }
          }}
          className={`primary-button${isDisabled ? " is-disabled" : ""}`}
          disabled={isDisabled}
          aria-disabled={isDisabled}
        >
          Filter
        </button>
      );
    } else {
      return (
        <div className="pull-up-element-2">
          <CustomLoader loadingText="Submitting..." />
        </div>
      );
    }
  };

  const handleDownloadCsv = () => {
    props.onDownloadCsv(getFilters());
  };

  const downloadCsvButton = () => {
    if (props.canDownloadCsv) {
      const hasFilters = HELPER.HAS_FILTER_VALUES(transactionFilters);
      const isDisabled = !dateRangeValidation.isValid || !hasFilters;

      return (
        <button
          onClick={() => {
            if (!isDisabled) {
              handleDownloadCsv();
            }
          }}
          className={`tertiary-button${isDisabled ? " is-disabled" : ""}`}
          disabled={props.isDownloading || isDisabled}
          aria-disabled={isDisabled}
        >
          <i className="pi pi-download" />
          <span className="hide-btn-text"> Download CSV</span>
        </button>
      );
    }
    return null;
  };

  const viewAlert = () => {
    if (errorMessage) {
      return (
        <div>
          <CustomToast title="Error" description={errorMessage} type="error" />
        </div>
      );
    } else {
      return <div />;
    }
  };

  const transactionsFilterForm = () => {
    return (
      <div>
        <div className="custom-modal-title p-text-left">Filter</div>
        <div className="custom-dialog-subtitle-container p-mb-5"></div>
        <div className="p-pb-1">{viewAlert()}</div>
        <div className="p-grid">
          {/* <div className="p-col-12">
            <FormInput
              value={transactionFilters["paymentFacilitatorId"]}
              required={false}
              field="paymentFacilitatorId"
              type="INPUT"
              error={formErrors["paymentFacilitatorId"]}
              fn={validateForm}
              loading={loading}
              placeholder="Payment facilitator Id"
            />
          </div> */}
          <Optional show={props.userType === UserTypes.Backoffice}>
            <div className="p-col-12">
              <FormDropdown
                required={true}
                label="paymentFacilitatorName"
                field="paymentFacilitatorId"
                error={formErrors["paymentFacilitatorId"]}
                disabled={loading}
                value={transactionFilters["paymentFacilitatorId"]}
                fn={validateDropdown}
                options={aggregators}
                placeholder="Select payment facilitator"
                filter
                filterBy="paymentFacilitatorName"
              />
            </div>
          </Optional>

          <div className="p-col-12">
            <FormDropdown
              required={false}
              field="status"
              error={null}
              disabled={false}
              value={transactionFilters["status"]}
              fn={validateDropdown}
              options={transactionStatuses}
              placeholder="Select transaction status"
            />
          </div>
          <div className="p-col-12">
            <FormInput
              value={transactionFilters["amount"]}
              required={false}
              field="amount"
              type="CASH_INPUT"
              error={formErrors["amount"]}
              fn={validateForm}
              loading={loading}
              placeholder="Amount"
            />
          </div>
          <div className="p-col-12">
            <FormInput
              value={transactionFilters["transactionReference"]}
              required={false}
              field="transactionReference"
              type="INPUT"
              error={formErrors["transactionReference"]}
              fn={validateForm}
              loading={loading}
              placeholder="Transaction reference"
            />
          </div>

          <div className="p-col-12">
            <FormInput
              value={transactionFilters["facilitatorTransactionReference"]}
              required={false}
              field="facilitatorTransactionReference"
              type="INPUT"
              error={formErrors["facilitatorTransactionReference"]}
              fn={validateForm}
              loading={loading}
              placeholder="Payment facilitator reference"
            />
          </div>
          <div className="p-col-6">
            <label>Start date</label>
            <FormInput
              inputType="date"
              value={transactionFilters["transactionStartDate"]}
              required={false}
              field="transactionStartDate"
              type="INPUT"
              error={formErrors["transactionStartDate"]}
              fn={validateForm}
              loading={loading}
              placeholder="Start date"
            />
          </div>
          <div className="p-col-6">
            <label>End date</label>
            <FormInput
              inputType="date"
              value={transactionFilters["transactionEndDate"]}
              required={false}
              field="transactionEndDate"
              type="INPUT"
              error={formErrors["transactionEndDate"]}
              fn={validateForm}
              loading={loading}
              placeholder="End date"
            />
          </div>
          {dateRangeError && (
            <div className="p-col-12 p-text-left">
              <p className="p-error p-my-0">{dateRangeError}</p>
            </div>
          )}
          <div className="p-col-12 p-text-left">
            <p className="text-small" style={{ color: "#5D7F8D", marginTop: "-0.5rem" }}>
              <i className="pi pi-info-circle" style={{ marginRight: "0.5rem" }} />
              You can only filter and download transactions within a maximum range of 90 days.
            </p>
          </div>
          <div className="p-col-12">
            <div className="p-mt-3">
              <div className="p-grid">
                <div className={loading ? "p-col-12" : props.canDownloadCsv ? "p-col-4" : "p-col-6"}>
                  {cancelButton()}
                </div>
                {props.canDownloadCsv && (
                  <div className={loading ? "p-col-12" : "p-col-4"}>{downloadCsvButton()}</div>
                )}
                <div className={loading ? "p-col-12" : props.canDownloadCsv ? "p-col-4" : "p-col-6"}>
                  {submitButton()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const transactionsFilterView = () => {
    switch (currentIndex) {
      case 0:
        return <>{transactionsFilterForm()}</>;
      default:
        return <div></div>;
    }
  };

  return (
    <div>
      <div>
        <Toast ref={toast} />
      </div>
      <div className="p-pb-2">{transactionsFilterView()}</div>
    </div>
  );
}
